import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  FileText,
  CheckCircle2,
  Clock,
  Shield,
  ArrowRight,
  BarChart3,
  FileSearch,
  Calendar,
} from 'lucide-react';

const features = [
  {
    icon: FileSearch,
    title: 'Analyse automatique',
    description: "Extraction intelligente des informations critiques de vos DCE en quelques minutes.",
  },
  {
    icon: CheckCircle2,
    title: 'Décision GO / NO GO',
    description: 'Recommandation claire basée sur l\'attractivité économique et la faisabilité technique.',
  },
  {
    icon: Calendar,
    title: 'Planning macro',
    description: 'Génération automatique d\'un planning avec validation de votre capacité opérationnelle.',
  },
  {
    icon: Shield,
    title: 'Traçabilité des sources',
    description: 'Chaque information est liée à sa source dans le document original.',
  },
];

const plans = [
  {
    name: 'Novice',
    price: '49',
    priceAnnual: '490',
    description: 'Pour découvrir l\'outil',
    features: [
      'Analyse GO / NO GO',
      'Identification des risques',
      'Aperçu global',
      'Résumé simplifié du projet',
      '4 DCE / mois',
      'Support basique',
    ],
    cta: 'Commencer',
    popular: false,
    badge: null,
  },
  {
    name: 'Pro',
    price: '169',
    priceAnnual: '1 900',
    description: 'Idéal pour optimiser vos réponses aux appels d\'offres',
    features: [
      'DCE illimités',
      'Analyse complète',
      'Planning macro détaillé (Gantt)',
      'Estimation des coûts',
      'Rapport PDF',
      'Gain de temps considérable',
      'Décision rapide GO / NO GO',
      'Sécurisation des marges',
    ],
    cta: 'Choisir Pro',
    popular: true,
    badge: 'Offre la plus utilisée',
  },
  {
    name: 'Expert',
    price: '289',
    priceAnnual: '3 200',
    description: 'Solution complète pour entreprises structurées avec plusieurs projets en parallèle',
    features: [
      'DCE illimités',
      'Analyse avancée approfondie',
      'Priorité de traitement',
      'Vision stratégique globale',
      'Optimisation de rentabilité',
      'Planning macro détaillé (Gantt)',
      'Estimation des coûts',
      'Rapport PDF',
      'Gain de temps considérable',
      'Décision rapide GO / NO GO',
      'Sécurisation des marges',
    ],
    cta: 'Choisir Expert',
    popular: false,
    badge: 'Offre premium pour entreprises structurées',
  },
];

export function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-slate-900">DCE Analyzer</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" data-testid="login-link">Connexion</Button>
            </Link>
            <Link to="/signup">
              <Button data-testid="signup-link">Commencer gratuitement</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                Analysez un DCE en 5 minutes
                <span className="text-primary"> et prenez une décision GO / NO GO fiable</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Obtenez instantanément un GO / NO GO, identifiez les risques, estimez les coûts
                et générez un planning en quelques minutes.
              </p>
              
              {/* Section statistique */}
              <div className="mt-8 p-6 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-slate-700 font-medium">
                  <span className="text-amber-600">⚠️</span> En moyenne, seulement <span className="text-primary font-bold">1 appel d'offres sur 5</span> est remporté.
                </p>
                <p className="mt-2 text-slate-600 text-sm">
                  L'enjeu n'est pas seulement de sélectionner les bons DCE,
                  mais d'en traiter suffisamment pour sécuriser vos chances de réussite.
                </p>
              </div>
              
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/signup">
                  <Button size="lg" className="gap-2" data-testid="hero-cta">
                    Analyser mon DCE gratuitement
                    <ArrowRight size={20} />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="ghost" className="text-slate-500 hover:text-slate-700" data-testid="hero-login">
                    J'ai déjà un compte
                  </Button>
                </Link>
              </div>
              <div className="mt-4 flex flex-col gap-1 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-green-500" />
                  <span>Test en conditions réelles – sans engagement</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-green-500" />
                  <span>Adapté aux entreprises du BTP</span>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Clock size={16} />
                  <span>Analyse en 5 min</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} />
                  <span>Décision claire</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={16} />
                  <span>Données sécurisées</span>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 size={16} />
                <span>Utilisé par des professionnels du BTP</span>
              </div>
              
              {/* Phrase d'accroche sous les badges */}
              <p className="mt-6 text-slate-600 text-sm font-medium">
                Un mauvais chantier peut vous faire perdre plusieurs milliers d'euros.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1748273491101-9d1db5d1814d?w=800&q=80"
                alt="Chantier de construction"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">GO RECOMMANDÉ</p>
                    <p className="text-sm text-slate-500">Score: 78/100</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bannière de test */}
      <div className="bg-primary/10 border-y border-primary/20 py-4">
        <div className="container mx-auto px-6 text-center">
          <p className="text-primary font-medium">
            Phase de test en cours – accès réservé à un nombre limité d'entreprises
          </p>
        </div>
      </div>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl font-bold text-slate-900">
              Tout ce dont vous avez besoin pour décider
            </h2>
            <p className="mt-4 text-slate-600">
              Une analyse complète de vos DCE avec des recommandations claires et traçables.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="p-6 bg-slate-50 rounded-xl border border-slate-100 hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Phrase d'impact */}
      <div className="bg-slate-900 py-12">
        <div className="container mx-auto px-6 text-center">
          <p className="text-white text-xl font-medium">
            Un mauvais chantier peut coûter plusieurs milliers d'euros.
          </p>
          <p className="text-slate-300 mt-2">
            Une bonne décision ne devrait pas prendre plusieurs heures.
          </p>
        </div>
      </div>

      {/* Pricing */}
      <section className="py-20 bg-slate-50" id="pricing">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl font-bold text-slate-900">
              Des tarifs adaptés à votre activité
            </h2>
            <p className="mt-4 text-slate-600">
              Choisissez le plan qui correspond à vos besoins.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl p-8 border-2 transition-all hover:shadow-xl ${
                  plan.popular ? 'border-primary shadow-lg' : 'border-slate-200'
                }`}
              >
                {plan.badge && (
                  <div className={`absolute -top-4 left-1/2 -translate-x-1/2 text-white text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap ${
                    plan.popular ? 'bg-primary' : 'bg-slate-700'
                  }`}>
                    {plan.badge}
                  </div>
                )}
                <h3 className="font-heading text-xl font-bold text-slate-900">{plan.name}</h3>
                <p className="text-slate-500 text-sm mt-1">{plan.description}</p>
                <div className="mt-6">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}€</span>
                  <span className="text-slate-500">/mois</span>
                </div>
                {plan.priceAnnual && (
                  <p className="text-sm text-slate-500 mt-1">
                    ou {plan.priceAnnual}€/an
                  </p>
                )}
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="block mt-8">
                  <Button
                    className="w-full"
                    variant={plan.popular ? 'default' : 'outline'}
                    data-testid={`plan-${plan.name.toLowerCase()}-btn`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-slate-600">
              Besoin d'un accompagnement spécifique ?{' '}
              <a href="mailto:contact@dce-analyzer.fr" className="text-primary font-semibold hover:underline">
                Contactez-nous
              </a>
            </p>
          </div>
          
          {/* Section early adopters */}
          <div className="mt-12 p-6 bg-white rounded-xl border-2 border-primary/20 max-w-2xl mx-auto text-center">
            <p className="text-slate-700 font-medium">
              Je recherche actuellement <span className="text-primary font-bold">3 à 5 entreprises</span> pour tester l'outil en conditions réelles.
            </p>
            <p className="mt-2 text-slate-500 text-sm">
              Accès prioritaire aux premiers utilisateurs.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">DCE Analyzer</span>
            </div>
            <p className="text-slate-400 text-sm text-center">
              DCE Analyzer fournit une aide à la décision basée sur l'analyse automatisée des
              documents. Les recommandations ne constituent pas un engagement.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
